/**
 * Eagerly-resolved, Vite-fingerprinted URLs for all b2b brand theme CSS files.
 *
 * import.meta.glob with { query: '?url', eager: true } causes Vite to:
 *   1. Discover all matching files at build time.
 *   2. Emit each as a separate hashed asset (e.g. foodora-light-Abc123.css).
 *   3. Replace each entry with the resolved public URL string.
 *
 * At runtime, look up the URL for the active brand/mode and set it on a
 * <link rel="stylesheet"> in <head>. Only one sheet is linked per request;
 * all others are present in the bundle but never fetched.
 *
 * Glob patterns are relative to this file (apps/portal/app/lib/).
 * ../../node_modules resolves to apps/portal/node_modules, where pnpm
 * symlinks @deliveryhero/cape-tokens as a direct dependency of the portal
 * package (it is not hoisted to the monorepo root). Vite preserves relative
 * paths as map keys, so the lookup key must use the same relative form.
 */

import type { Brand, Mode } from "~/lib/brand";

const THEMES_PATH = "../../node_modules/@deliveryhero/cape-tokens/themes";

const lightUrls = import.meta.glob(
  "../../node_modules/@deliveryhero/cape-tokens/themes/*/light.css",
  { query: "?url", import: "default", eager: true },
) as Record<string, string>;

const darkUrls = import.meta.glob(
  "../../node_modules/@deliveryhero/cape-tokens/themes/*/dark.css",
  { query: "?url", import: "default", eager: true },
) as Record<string, string>;

/**
 * Returns the fingerprinted public URL for the given brand + mode CSS file,
 * or undefined if Vite did not resolve it (should not happen for valid inputs).
 */
export function getThemeUrl(brand: Brand, mode: Mode): string | undefined {
  const key = `${THEMES_PATH}/${brand}/${mode}.css`;
  return mode === "light" ? lightUrls[key] : darkUrls[key];
}
